<template>
  <div class="flex flex-col min-h-screen items-center">
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="ml-4 sm:ml-0">
        <LogoIcon />
      </div>
      <div class="h-12 mt-5 mb-5 mr-4 sm:mr-8">
        <LanguageSwitch />
      </div>
    </div>
    <div
      v-if="isDomainError"
      class="max-w-[483px] w-full text-center flex-grow px-4"
    >
      <h2 class="my-6 text-center">
        {{ $t('messages.entra_id_verification.success_result.title') }}
      </h2>
      <div class="bg-white rounded-lg">
        <div class="mb-6 flex flex-col items-center flex-shrink-0 text-center">
          <p class="b1 text-zyxel-gray-900 mt-6">
            <strong> {{
              $t('messages.entra_id_verification.domain_mismatch_error.error_subtitle') }}
            </strong>
          </p>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-6">
          {{ $t('messages.entra_id_verification.domain_mismatch_error.error_description') }}
        </p>
        <strong class="b1 block text-left text-zyxel-gray-900 font-bold ml-6 mr-6 mb-6 pb-6">
          {{ $t('messages.entra_id_verification.domain_mismatch_error.error_highlight') }}
        </strong>
      </div>
    </div>
    <div
      v-else-if="isEmailError"
      class="max-w-[483px] w-full text-center flex-grow px-4"
    >
      <h2 class="my-6 text-center">
        {{ $t('messages.entra_id_verification.success_result.title') }}
      </h2>
      <div class="bg-white rounded-lg">
        <div class="mb-6 flex flex-col items-center flex-shrink-0 text-center">
          <p class="b1 text-zyxel-gray-900 mt-6">
            <strong> {{
              $t('messages.entra_id_verification.email_mismatch_error.error_subtitle') }}
            </strong>
          </p>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-6">
          {{
            $t('messages.entra_id_verification.email_mismatch_error.error_description') }}
        </p>
        <strong class="b1 block text-left text-zyxel-gray-900 font-bold ml-6 mr-6 mb-6 pb-6">{{
          $t('messages.entra_id_verification.email_mismatch_error.error_highlight') }}</strong>
      </div>
    </div>
    <div
      v-else-if="isConfigError"
      class="max-w-[483px] w-full text-center flex-grow px-4"
    >
      <h2 class="my-6 text-center">
        {{ $t('messages.entra_id_verification.success_result.title') }}
      </h2>
      <div class="bg-white rounded-lg">
        <div class="mb-6 flex flex-col items-center flex-shrink-0 text-center">
          <p class="b1 text-zyxel-gray-900 mt-6">
            <strong> {{
              $t('messages.entra_id_verification.config_error.error_subtitle') }}
            </strong>
          </p>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-6 pb-6">
          {{
            $t('messages.entra_id_verification.config_error.error_description') }}
        </p>
      </div>
    </div>
    <div
      v-else
      class="max-w-[483px] w-full text-center flex-grow px-4"
    >
      <h2 class="my-6 text-center">
        {{ $t('messages.entra_id_verification.success_result.title') }}
      </h2>
      <div class="bg-white rounded-lg">
        <div class="mb-6 flex flex-col items-center flex-shrink-0 text-center">
          <p class="b1 text-zyxel-gray-900 mt-6">
            <strong> {{
              $t('messages.entra_id_verification.success_result.success_subtitle') }}
            </strong>
          </p>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-6">
          {{
            $t('messages.entra_id_verification.success_result.success_description') }}
          <span class="text-zyxel-gray-900 font-bold"> {{
            $t('messages.entra_id_verification.success_result.success_highlight') }}</span>
        </p>
        <div class="text-left ml-6 mb-6">
          <ul class="list-disc ml-5 text-zyxel-gray-800">
            <li><strong>{{ $t('messages.entra_id_verification.success_result.domain') }}:</strong> {{ domain }}</li>
            <li><strong>{{ $t('messages.entra_id_verification.success_result.user_count') }}:</strong> {{ user_count }}</li>
          </ul>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-2">
          {{
            $t('messages.entra_id_verification.success_result.deployment_instructions') }}
        </p>
        <div class="ml-6 mr-6 mb-2 bg-gray-100 rounded-lg border border-gray-800 flex">
          <div class="w-4/5 flex-grow text-left">
            <p class="b2 text-zyxel-gray-800 mt-2 ml-4">
              {{ $t("messages.entra_id_verification.success_result.deployment_code_label") }}
            </p>
            <p class="b2 text-zyxel-gray-800 mb-2 ml-4 h-1/2">
              {{ deployment_code }}
            </p>
          </div>
          <div class="w-1/5 flex items-center justify-center">
            <button
              class="b2 text-zyxel-green-400 cursor-pointer ml-4 flex items-center"
              aria-label="copy deployment code"
              @click="copyDeploymentCode"
            >
              <template v-if="!copied">
                <img
                  class="mr-1 h-5 w-5"
                  src="@/assets/svg/icon_copy_green.svg"
                  alt="copy icon"
                >
              </template>
              <template v-else>
                {{ $t("messages.entra_id_verification.success_result.copied") }}
              </template>
            </button>
          </div>
        </div>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 mb-6">
          {{
            $t('messages.entra_id_verification.success_result.deployment_code_expire_notice') }}
        </p>
        <p class="b1 block text-left text-zyxel-gray-800 ml-6 mr-6 pb-6">
          {{
            $t('messages.entra_id_verification.success_result.auth_method_notice') }}
        </p>
      </div>
    </div>
    <ZyxelFooter />
  </div>
</template>
<script>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import LogoIcon from '@/components/LogoIcon.vue';
import LanguageSwitch from "@/components/LanguageSwitch.vue";
import ZyxelFooter from "@/components/ZyxelFooter.vue";
import { logger } from '@/utils/logger';

export default {
    name: 'EntraIdVerifyResultPage',
    components: {
        LogoIcon,
        LanguageSwitch,
        ZyxelFooter,
    },
    setup() {
        const route = useRoute();
        const response_code = route.query.res_code;
        const deployment_code = route.query.d_code;
        const domain = route.query.domain;
        const user_count = route.query.u_count;

        const isDomainError = ref(false);
        const isEmailError = ref(false);
        const isConfigError = ref(false);
        const copied = ref(false);

        if (response_code == 1) {
            isDomainError.value = true;
        } else if (response_code == 2) {
            isEmailError.value = true;
        } else if (response_code == 3) {
            isConfigError.value = true;
        }

        return { deployment_code, domain, user_count, isDomainError, isEmailError, copied, isConfigError };
    },
    methods: {
        async copyDeploymentCode() {
            if (!navigator.clipboard) {
                logger.warn("此瀏覽器不支援剪貼簿功能，請手動複製。")
                return;
            }

            try {
                await navigator.clipboard.writeText(this.deployment_code)
                this.copied = true
                setTimeout(() => this.copied = false, 2000) // 2秒後切回「Copy」
            } catch (err) {
                logger.error('copy fail: ', err)
            }
        },
    },
}
</script>